/**
 * Attachment Storage Utilities
 *
 * Provides functions for uploading chat attachments (PDFs, DOCXs) to Firebase Storage
 * and serializing them with storage URLs for cross-device/cross-session persistence.
 *
 * This solves the problem of attachments being unavailable after page refresh when
 * loaded from Firestore (which can't store the base64 dataUrl due to 1MB doc limit).
 */

import {
  storage,
  getAttachmentStoragePath,
  uploadBytes,
  getDownloadURL,
  ref,
} from './firebaseStorage';
import { getAuth } from 'firebase/auth';
import type { SerializedAttachment } from '../components/GlobalChatSystem/types';
import { isInlineEligible } from '../services/prest0nSession/client';

// Must match the CF base URL used in src/services/prest0nSession/client.ts
const CF_BASE_URL = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';

/**
 * Result of a storage upload operation
 */
export interface StorageUploadResult {
  /** Whether the upload succeeded */
  success: boolean;
  /** Firebase Storage download URL (if successful) */
  storageUrl?: string;
  /** Storage path for reference/deletion (if successful) */
  storagePath?: string;
  /** Error details (if failed) */
  error?: Error;
}

/**
 * Serialized attachment with Firebase Storage fields
 */
export interface AttachmentWithStorage extends SerializedAttachment {
  /** Firebase Storage download URL */
  storageUrl?: string;
  /** Storage path for reference/deletion */
  storagePath?: string;
}

/**
 * Upload a file directly to GCS via a pre-signed URL.
 *
 * Phase A: Cloud Parallel Ingestion — browser uploads directly to GCS,
 * bypassing the Cloud Function proxy body entirely. No body size limit.
 *
 * Flow:
 *   1. POST to prest0nGenerateUploadUrls Cloud Function with file name
 *   2. Receive signed URL + gcsPath
 *   3. PUT file directly to GCS using signed URL
 *   4. Return gcsPath for inclusion in turn request
 *
 * @param file - The File object to upload
 * @param sessionKey - Current session key
 * @param accountPath - Account path for GCS path scoping
 * @returns Upload result with gcsPath (if successful)
 */
export async function uploadAttachmentViaGcsSignedUrl(
  file: File,
  sessionKey: string,
  accountPath: string,
): Promise<StorageUploadResult & { gcsPath?: string }> {
  const auth = getAuth();
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    return { success: false, error: new Error('No auth token for GCS upload') };
  }

  try {
    // Step 1: Get signed URL from Cloud Function
    const urlResponse = await fetch(
      `${CF_BASE_URL}/prest0nGenerateUploadUrls`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileNames: [file.name],
          sessionKey,
          accountPath,
          mimeTypes: [file.type || 'application/octet-stream'],
        }),
        signal: AbortSignal.timeout(15_000),
      },
    );

    if (!urlResponse.ok) {
      const errText = await urlResponse.text().catch(() => 'unknown');
      throw new Error(`Signed URL request failed (${urlResponse.status}): ${errText}`);
    }

    const { urls } = (await urlResponse.json()) as {
      urls: Array<{ fileName: string; signedUrl: string; gcsPath: string; expiresAt: number }>;
    };

    if (!urls || urls.length === 0) {
      throw new Error('No signed URL returned');
    }

    const { signedUrl, gcsPath } = urls[0];

    // Step 2: PUT file directly to GCS
    const arrayBuffer = await file.arrayBuffer();
    const putResponse = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: arrayBuffer,
      signal: AbortSignal.timeout(300_000), // 5 min for large files
    });

    if (!putResponse.ok) {
      const errText = await putResponse.text().catch(() => 'unknown');
      throw new Error(`GCS PUT failed (${putResponse.status}): ${errText}`);
    }

    console.log(`[AttachmentStorage] GCS upload succeeded: ${gcsPath}`);
    return {
      success: true,
      gcsPath,
      storagePath: gcsPath, // reuse field for display
    };
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.warn('[AttachmentStorage] GCS signed URL upload failed:', err.message);
    return { success: false, error: err };
  }
}
/**
 * @param file - The File object to upload
 * @param uid - User ID (Firebase Auth UID)
 * @param messageId - Unique message identifier
 * @returns Upload result with download URL and storage path
 */
async function uploadAttachmentViaProxy(
  file: File,
  uid: string,
  messageId: string
): Promise<StorageUploadResult> {
  const auth = getAuth();
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    return { success: false, error: new Error('No auth token for proxy upload') };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60_000);

  try {
    const arrayBuffer = await file.arrayBuffer();
    const params = new URLSearchParams({
      filename: file.name,
      messageId,
      contentType: file.type || 'application/octet-stream',
    });

    const response = await fetch(
      `${CF_BASE_URL}/prest0nUploadFile?${params.toString()}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/octet-stream',
        },
        body: arrayBuffer,
        signal: controller.signal,
      }
    );

    if (!response.ok) {
      const errBody = await response.text().catch(() => 'unknown error');
      throw new Error(`Proxy upload failed (${response.status}): ${errBody}`);
    }

    const result = await response.json();
    console.log('[AttachmentStorage] Proxy upload succeeded');
    return {
      success: true,
      storageUrl: result.storageUrl,
      storagePath: result.storagePath,
    };
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.warn('[AttachmentStorage] Proxy upload failed:', err.message);
    return { success: false, error: err };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Upload a single file to Firebase Storage with retry logic.
 * Tries the server-side CF proxy first (immune to ad-blockers),
 * then falls back to direct Firebase Storage SDK upload.
 *
 * @param file - The File object to upload
 * @param uid - User ID (Firebase Auth UID)
 * @param messageId - Unique message identifier
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @returns Upload result with download URL and storage path
 */
export async function uploadAttachmentToStorage(
  file: File,
  uid: string,
  messageId: string,
  maxRetries: number = 3
): Promise<StorageUploadResult> {
  // ── Strategy 1: Server-side proxy upload (ad-blocker immune) ──
  const proxyResult = await uploadAttachmentViaProxy(file, uid, messageId);
  if (proxyResult.success) {
    return proxyResult;
  }
  console.warn('[AttachmentStorage] Proxy failed, falling back to direct SDK upload');

  // ── Strategy 2: Direct Firebase Storage SDK upload (original path) ──
  let lastError: Error | undefined;

  // Per-attempt timeout for uploadBytes. Without this, a wedged connection
  // can block a single attempt indefinitely, which in turn stalls all 3
  // retries and (upstream) the deep-research finalize path.
  const UPLOAD_BYTES_TIMEOUT_MS = 45_000;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const storagePath = getAttachmentStoragePath(uid, messageId, file.name);
      const storageRef = ref(storage, storagePath);

      // Upload the file with metadata, racing against a 45s timeout.
      // On timeout we fall through to the existing retry-loop backoff.
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const uploadPromise = uploadBytes(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          originalName: file.name,
          uploadedAt: new Date().toISOString(),
          size: file.size.toString(),
        },
      });
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(
            new Error(
              `uploadBytes timed out after ${UPLOAD_BYTES_TIMEOUT_MS}ms`
            )
          );
        }, UPLOAD_BYTES_TIMEOUT_MS);
      });

      try {
        await Promise.race([uploadPromise, timeoutPromise]);
      } catch (raceError) {
        if (raceError instanceof Error && raceError.message.includes('timed out')) {
          console.warn(
            `[AttachmentStorage] Direct SDK upload attempt ${attempt + 1} timed out after 45s`
          );
        }
        throw raceError;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }

      // Get the download URL
      const storageUrl = await getDownloadURL(storageRef);

      if (attempt > 0) {
        console.log(`[AttachmentStorage] Upload succeeded on retry attempt ${attempt + 1}/${maxRetries}`);
      }

      return {
        success: true,
        storageUrl,
        storagePath,
      };
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.warn(`[AttachmentStorage] Upload attempt ${attempt + 1}/${maxRetries} failed:`, lastError.message);

      if (attempt < maxRetries - 1) {
        // Exponential backoff: 1s, 2s, 4s
        const backoffMs = 1000 * Math.pow(2, attempt);
        console.log(`[AttachmentStorage] Retrying in ${backoffMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, backoffMs));
      }
    }
  }

  console.error('[AttachmentStorage] All upload attempts failed:', lastError);
  return {
    success: false,
    error: lastError,
  };
}

/**
 * Upload multiple files to Firebase Storage in parallel.
 *
 * @param files - Array of File objects to upload
 * @param uid - User ID (Firebase Auth UID)
 * @param messageId - Unique message identifier
 * @returns Array of upload results (same order as input files)
 */
export async function uploadAttachmentsToStorage(
  files: File[],
  uid: string,
  messageId: string
): Promise<StorageUploadResult[]> {
  return Promise.all(
    files.map((file) => uploadAttachmentToStorage(file, uid, messageId))
  );
}

/**
 * Serialize a File object with Firebase Storage upload.
 * Creates a SerializedAttachment with storage URL for cross-device persistence.
 *
 * @param file - The File object to serialize and upload
 * @param uid - User ID (Firebase Auth UID)
 * @param messageId - Unique message identifier
 * @returns Serialized attachment with storage URL (if upload succeeded)
 */
export async function serializeAttachmentWithStorage(
  file: File,
  uid: string,
  messageId: string
): Promise<AttachmentWithStorage> {
  // Generate base64 dataUrl as fallback for localStorage persistence
  // This ensures attachments remain downloadable even if storageUrl becomes unavailable
  // Firestore will strip dataUrl (too large), but localStorage preserves it via Fix 3 merge logic
  //
  // …but only for a file small enough for that fallback to exist (2026-09-09).
  // localStorage stops around 5 MB, so base64 of a multi-MB file is not a
  // fallback anywhere — it is read, held, and dropped. This is the SECOND
  // read of the same bytes on the cloud-upload path (`serializeAttachments`
  // in useGlobalChat did the first), which is how the 398 MB / 50-clip run
  // reached ~1 GB of live strings and killed the tab. Same ceiling, same
  // reason: above it an inline copy can never be used.
  const dataUrl = isInlineEligible(file.size)
    ? await new Promise<string | undefined>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : undefined);
      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(file);
    })
    : undefined;

  try {
    // Upload to Firebase Storage
    const uploadResult = await uploadAttachmentToStorage(file, uid, messageId);

    // Return serialized attachment with storage info AND dataUrl fallback
    return {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
      // Primary: Firebase Storage URL for cross-device persistence
      storageUrl: uploadResult.storageUrl,
      storagePath: uploadResult.storagePath,
      // When the signed URL was minted — the image tile re-mints past six days
      // so a picture never breaks on day 8 (chat-thread PR-6, 2026-09-09).
      storageUrlMintedAt: Date.now(),
      // Fallback: base64 dataUrl for localStorage persistence
      // Stripped by Firestore (1MB limit) but preserved in localStorage
      dataUrl,
    };
  } catch (error) {
    // If upload fails completely (e.g., CORS error), return with dataUrl fallback
    console.warn('[AttachmentStorage] serializeAttachmentWithStorage failed:', error);
    return {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
      // No storageUrl - dataUrl serves as the only download source
      dataUrl,
    };
  }
}

/**
 * Serialize multiple File objects with Firebase Storage upload.
 *
 * @param files - Array of File objects to serialize and upload
 * @param uid - User ID (Firebase Auth UID)
 * @param messageId - Unique message identifier
 * @returns Array of serialized attachments with storage URLs
 */
export async function serializeAttachmentsWithStorage(
  files: File[],
  uid: string,
  messageId: string
): Promise<AttachmentWithStorage[]> {
  return Promise.all(
    files.map((file) => serializeAttachmentWithStorage(file, uid, messageId))
  );
}

/**
 * Check if the current user is authenticated for Storage uploads.
 * Returns the user's UID if authenticated, undefined otherwise.
 *
 * FH-QA-T2.1 (2026-05-21): rewritten to use the already-imported
 * `getAuth` from `firebase/auth` rather than a dynamic CommonJS
 * `require('firebase/auth')`. In some Webpack/Next/Vite production
 * bundle configurations the `require()` call cannot resolve and
 * throws synchronously — which, when invoked from inside
 * `handleSend()` in useGlobalChat, took down the entire post-bubble
 * send pipeline (attachment upload AND the orchestrator turn POST
 * to prest0nRunTurn) before any user-visible error could surface.
 * That produced the exact 12:46 UTC repro Malaika hit on Fernando's
 * webchat: file pill renders, user bubble renders, then complete
 * silence — no upload, no turn, no error.
 *
 * Bonus: hard-wrap in try/catch so any future auth-init edge case
 * degrades to "no upload" rather than "no reply at all".
 */
export function getCurrentUserUid(): string | undefined {
  try {
    const auth = getAuth();
    return auth.currentUser?.uid;
  } catch (err) {
    console.warn('[AttachmentStorage] getCurrentUserUid threw — returning undefined:', err);
    return undefined;
  }
}
